import React, { createContext, useContext, useState, useEffect } from 'react';

// Translation files
const translations = {
  en: {
    common: {
      selected: 'Selected',
      loading: 'Loading...',
      viewAll: 'View All',
      clearSelection: 'Clear Selection',
      newChat: 'New Chat',
      ready: 'Ready',
      processing: 'Processing...',
      refresh: 'Refresh',
    },
    // Navigation & UI
    nav: {
      dashboard: 'Dashboard',
      preferences: 'Preferences',
      settings: 'Settings',
      files: 'My Files',
      chatHistory: 'Chat History',
      login: 'Login',
      register: 'Register',
      logout: 'Logout',
      upgradePromo: 'Upgrade to Pro'
    },
    
    // Chat Interface
    chat: {
      startConversation: 'Start a Conversation',
      askQuestions: 'Ask questions about your uploaded documents',
      typeQuestion: 'Type your question about the documents...',
      send: 'Send',
      searching: 'Searching documents...',
      newChat: 'Start New Chat',
      sources: 'Sources',
      results: 'results'
    },
    
    // Document Management
    documents: {
      title: 'Document Manager',
      uploadNew: 'Upload New Document',
      adminOnly: 'Admin Only',
      adminRequired: 'Administrator Access Required',
      adminDescription: 'Document upload and deletion are restricted to administrators only. Please contact your system administrator if you need to upload or manage documents.',
      currentRole: 'Current role',
      choosePDF: 'Choose PDF File',
      dropPDF: 'Drop PDF Here',
      dragDrop: 'or drag and drop a PDF document',
      uploading: 'Uploading...',
      processing: 'Processing...',
      noDocuments: 'No documents uploaded yet',
      uploadToStart: 'Upload a PDF to get started',
      processSelected: 'Process Selected Document',
      processDocument: 'Process Document',
      yourDocuments: 'Your Documents',
      viewJson: 'View JSON',
      showDetails: 'Show Details'
    },
    
    // Preferences
    preferences: {
      title: 'Preferences',
      language: 'Language',
      theme: 'Theme',
      darkMode: 'Dark Mode',
      lightMode: 'Light Mode',
      switchToLight: 'Switch to light theme',
      switchToDark: 'Switch to dark theme',
      aiModel: 'AI Model',
      autoSave: 'Auto-save chats',
      notifications: 'Enable notifications',
      chatRetention: 'Chat retention (days)',
      responseLength: 'Response length',
      analysisDepth: 'Analysis depth',
      responseSettings: 'Response Settings',
      dataRetention: 'Data Retention',
      autoSaveDescription: 'Automatically save conversation history',
      notificationsDescription: 'Processing updates and alerts',
      chatRetentionDescription: 'How long to keep your chat history stored'
    },
    
    // Messages & Notifications
    messages: {
      uploadSuccess: 'uploaded successfully!',
      deleteSuccess: 'deleted successfully',
      processSuccess: 'Processing completed for',
      clauses: 'clauses extracted',
      loginRequired: 'Authentication required. Please log in.',
      serverError: 'Server error. Please try again in a few moments.',
      networkError: 'Unable to connect to the server. Please check your internet connection.',
      noDocumentsAvailable: 'No processed documents available for queries. Please upload and process documents first.',
      errorProcessing: 'Sorry, I encountered an error while processing your question. Please try again or make sure documents are uploaded and processed.'
    },
    
    // Forms & Validation
    forms: {
      email: 'Email',
      password: 'Password',
      firstName: 'First Name',
      lastName: 'Last Name',
      confirmPassword: 'Confirm Password',
      forgotPassword: 'Forgot Password?',
      fillAllFields: 'Please fill in all fields',
      invalidCredentials: 'Invalid email or password. Please check your credentials and try again.',
      accountNotFound: 'Account not found. Please check your email or create a new account.',
      continueWithGoogle: 'Continue with Google'
    },
    
    // Options & Selections
    options: {
      short: 'Short',
      medium: 'Medium',
      long: 'Long',
      basic: 'Basic',
      standard: 'Standard',
      detailed: 'Detailed',
      trial: 'Trial Period',
      proUser: 'Pro User',
      concise: 'Concise',
      comprehensive: 'Comprehensive',
      quickScan: 'Quick Scan',
      deepAnalysis: 'Deep Analysis',
      expertLevel: 'Expert Level'
    },
    
    // Profile & Settings
    profile: {
      profileInformation: 'Profile Information',
      companyInformation: 'Company Information',
      accountInformation: 'Account Information',
      security: 'Security',
      quickStats: 'Quick Stats',
      edit: 'Edit',
      save: 'Save',
      cancel: 'Cancel',
      saveChanges: 'Save Changes',
      changePassword: 'Change Password',
      currentPassword: 'Current Password',
      newPassword: 'New Password',
      confirmNewPassword: 'Confirm New Password',
      updatePassword: 'Update Password',
      cannotBeChanged: 'cannot be changed',
      phoneNumber: 'Phone Number',
      company: 'Company',
      department: 'Department',
      position: 'Position',
      location: 'Location',
      joinDate: 'Join Date',
      subscriptionStatus: 'Subscription Status',
      timezone: 'Timezone',
      accountRole: 'Account Role',
      administrator: 'Administrator',
      user: 'User',
      documentsUploaded: 'Documents Uploaded',
      chatsCreated: 'Chats Created',
      storageUsed: 'Storage Used',
      lastLogin: 'Last Login',
      today: 'Today',
      manageProfile: 'Manage your profile and account settings'
    }
  },
  
  es: {
    common: {
      selected: 'Seleccionado',
      loading: 'Cargando...',
      viewAll: 'Ver Todo',
      clearSelection: 'Borrar Selección',
      newChat: 'Nuevo Chat',
      ready: 'Listo',
      processing: 'Procesando...',
      refresh: 'Actualizar',
    },
    // Navigation & UI
    nav: {
      dashboard: 'Tablero',
      preferences: 'Preferencias',
      settings: 'Configuración',
      files: 'Mis Archivos',
      chatHistory: 'Historial de Chat',
      login: 'Iniciar Sesión',
      register: 'Registrarse',
      logout: 'Cerrar Sesión',
      upgradePromo: 'Actualizar a Pro'
    },
    
    // Chat Interface
    chat: {
      startConversation: 'Iniciar una Conversación',
      askQuestions: 'Haz preguntas sobre tus documentos subidos',
      typeQuestion: 'Escribe tu pregunta sobre los documentos...',
      send: 'Enviar',
      searching: 'Buscando documentos...',
      newChat: 'Nuevo Chat',
      sources: 'Fuentes',
      results: 'resultados'
    },
    
    // Document Management
    documents: {
      title: 'Gestor de Documentos',
      uploadNew: 'Subir Nuevo Documento',
      adminOnly: 'Solo Administrador',
      adminRequired: 'Acceso de Administrador Requerido',
      adminDescription: 'La carga y eliminación de documentos está restringida solo a administradores. Contacte a su administrador del sistema si necesita subir o gestionar documentos.',
      currentRole: 'Rol actual',
      choosePDF: 'Elegir Archivo PDF',
      dropPDF: 'Soltar PDF Aquí',
      dragDrop: 'o arrastra y suelta un documento PDF',
      uploading: 'Subiendo...',
      processing: 'Procesando...',
      noDocuments: 'Aún no se han subido documentos',
      uploadToStart: 'Sube un PDF para comenzar',
      processSelected: 'Procesar Documento Seleccionado',
      processDocument: 'Procesar Documento',
      yourDocuments: 'Tus Documentos',
      viewJson: 'Ver JSON',
      showDetails: 'Ver Detalles'
    },
    
    // Preferences
    preferences: {
      title: 'Preferencias',
      language: 'Idioma',
      theme: 'Tema',
      darkMode: 'Modo Oscuro',
      lightMode: 'Modo Claro',
      switchToLight: 'Cambiar a tema claro',
      switchToDark: 'Cambiar a tema oscuro',
      aiModel: 'Modelo de IA',
      autoSave: 'Guardar chats automáticamente',
      notifications: 'Habilitar notificaciones',
      chatRetention: 'Retención de chat (días)',
      responseLength: 'Longitud de respuesta',
      analysisDepth: 'Profundidad de análisis',
      responseSettings: 'Configuración de Respuesta',
      dataRetention: 'Retención de Datos',
      autoSaveDescription: 'Guardar automáticamente el historial de conversación',
      notificationsDescription: 'Actualizaciones de procesamiento y alertas',
      chatRetentionDescription: 'Cuánto tiempo mantener almacenado tu historial de chat'
    },
    
    // Messages & Notifications
    messages: {
      uploadSuccess: 'subido exitosamente!',
      deleteSuccess: 'eliminado exitosamente',
      processSuccess: 'Procesamiento completado para',
      clauses: 'cláusulas extraídas',
      loginRequired: 'Autenticación requerida. Por favor inicia sesión.',
      serverError: 'Error del servidor. Por favor intenta de nuevo en unos momentos.',
      networkError: 'No se puede conectar al servidor. Por favor verifica tu conexión a internet.',
      noDocumentsAvailable: 'No hay documentos procesados disponibles para consultas. Por favor sube y procesa documentos primero.',
      errorProcessing: 'Lo siento, encontré un error al procesar tu pregunta. Por favor intenta de nuevo o asegúrate de que los documentos estén subidos y procesados.'
    },
    
    // Forms & Validation
    forms: {
      email: 'Correo Electrónico',
      password: 'Contraseña',
      firstName: 'Nombre',
      lastName: 'Apellido',
      confirmPassword: 'Confirmar Contraseña',
      forgotPassword: '¿Olvidaste tu contraseña?',
      fillAllFields: 'Por favor completa todos los campos',
      invalidCredentials: 'Correo electrónico o contraseña inválidos. Por favor verifica tus credenciales e intenta de nuevo.',
      accountNotFound: 'Cuenta no encontrada. Por favor verifica tu correo electrónico o crea una nueva cuenta.',
      continueWithGoogle: 'Continuar con Google'
    },
    
    // Options & Selections
    options: {
      short: 'Corto',
      medium: 'Medio',
      long: 'Largo',
      basic: 'Básico',
      standard: 'Estándar',
      detailed: 'Detallado',
      trial: 'Período de Prueba',
      proUser: 'Usuario Pro',
      concise: 'Conciso',
      comprehensive: 'Completo',
      quickScan: 'Escaneo Rápido',
      deepAnalysis: 'Análisis Profundo',
      expertLevel: 'Nivel Experto'
    },
    
    // Profile & Settings
    profile: {
      profileInformation: 'Información del Perfil',
      companyInformation: 'Información de la Empresa',
      accountInformation: 'Información de la Cuenta',
      security: 'Seguridad',
      quickStats: 'Estadísticas Rápidas',
      edit: 'Editar',
      save: 'Guardar',
      cancel: 'Cancelar',
      saveChanges: 'Guardar Cambios',
      changePassword: 'Cambiar Contraseña',
      currentPassword: 'Contraseña Actual',
      newPassword: 'Nueva Contraseña',
      confirmNewPassword: 'Confirmar Nueva Contraseña',
      updatePassword: 'Actualizar Contraseña',
      cannotBeChanged: 'no se puede cambiar',
      phoneNumber: 'Número de Teléfono',
      company: 'Empresa',
      department: 'Departamento',
      position: 'Posición',
      location: 'Ubicación',
      joinDate: 'Fecha de Ingreso',
      subscriptionStatus: 'Estado de Suscripción',
      timezone: 'Zona Horaria',
      accountRole: 'Rol de Cuenta',
      administrator: 'Administrador',
      user: 'Usuario',
      documentsUploaded: 'Documentos Subidos',
      chatsCreated: 'Chats Creados',
      storageUsed: 'Almacenamiento Usado',
      lastLogin: 'Último Inicio de Sesión',
      today: 'Hoy',
      manageProfile: 'Gestiona tu perfil y configuración de cuenta'
    }
  },
  
  fr: {
    common: {
      selected: 'Sélectionné',
      loading: 'Chargement...',
      viewAll: 'Voir tout',
      clearSelection: 'Effacer la sélection',
      newChat: 'Nouveau Chat',
      ready: 'Prêt',
      processing: 'Traitement...',
      refresh: 'Actualiser',
    },
    // Navigation & UI
    nav: {
      dashboard: 'Tableau de bord',
      preferences: 'Préférences',
      settings: 'Paramètres',
      files: 'Mes Fichiers',
      chatHistory: 'Historique du Chat',
      login: 'Se Connecter',
      register: "S'inscrire",
      logout: 'Se Déconnecter',
      upgradePromo: 'Passer à Pro'
    },
    
    // Chat Interface
    chat: {
      startConversation: 'Démarrer une Conversation',
      askQuestions: 'Posez des questions sur vos documents téléchargés',
      typeQuestion: 'Tapez votre question sur les documents...',
      send: 'Envoyer',
      searching: 'Recherche dans les documents...',
      newChat: 'Nouveau Chat',
      sources: 'Sources',
      results: 'résultats'
    },
    
    // Document Management
    documents: {
      title: 'Gestionnaire de Documents',
      uploadNew: 'Télécharger un Nouveau Document',
      adminOnly: 'Admin Seulement',
      adminRequired: "Accès Administrateur Requis",
      adminDescription: "Le téléchargement et la suppression de documents sont réservés aux administrateurs uniquement. Veuillez contacter votre administrateur système si vous devez télécharger ou gérer des documents.",
      currentRole: 'Rôle actuel',
      choosePDF: 'Choisir un Fichier PDF',
      dropPDF: 'Déposer le PDF Ici',
      dragDrop: 'ou glissez-déposez un document PDF',
      uploading: 'Téléchargement...',
      processing: 'Traitement...',
      noDocuments: 'Aucun document téléchargé pour le moment',
      uploadToStart: 'Téléchargez un PDF pour commencer',
      processSelected: 'Traiter le Document Sélectionné',
      processDocument: 'Traiter le Document',
      yourDocuments: 'Vos Documents',
      viewJson: 'Voir JSON',
      showDetails: 'Afficher les détails'
    },
    
    // Preferences
    preferences: {
      title: 'Préférences',
      language: 'Langue',
      theme: 'Thème',
      darkMode: 'Mode Sombre',
      lightMode: 'Mode Clair',
      switchToLight: 'Passer au thème clair',
      switchToDark: 'Passer au thème sombre',
      aiModel: "Modèle d'IA",
      autoSave: 'Sauvegarde automatique des chats',
      notifications: 'Activer les notifications',
      chatRetention: 'Rétention du chat (jours)',
      responseLength: 'Longueur de réponse',
      analysisDepth: "Profondeur d'analyse",
      responseSettings: 'Paramètres de Réponse',
      dataRetention: 'Rétention des Données',
      autoSaveDescription: 'Sauvegarder automatiquement l\'historique des conversations',
      notificationsDescription: 'Mises à jour de traitement et alertes',
      chatRetentionDescription: 'Combien de temps conserver votre historique de chat stocké'
    },
    
    // Messages & Notifications
    messages: {
      uploadSuccess: 'téléchargé avec succès!',
      deleteSuccess: 'supprimé avec succès',
      processSuccess: 'Traitement terminé pour',
      clauses: 'clauses extraites',
      loginRequired: 'Authentification requise. Veuillez vous connecter.',
      serverError: 'Erreur serveur. Veuillez réessayer dans quelques instants.',
      networkError: 'Impossible de se connecter au serveur. Veuillez vérifier votre connexion internet.',
      noDocumentsAvailable: 'Aucun document traité disponible pour les requêtes. Veuillez télécharger et traiter des documents en premier.',
      errorProcessing: "Désolé, j'ai rencontré une erreur lors du traitement de votre question. Veuillez réessayer ou vous assurer que les documents sont téléchargés et traités."
    },
    
    // Forms & Validation
    forms: {
      email: 'E-mail',
      password: 'Mot de Passe',
      firstName: 'Prénom',
      lastName: 'Nom de Famille',
      confirmPassword: 'Confirmer le Mot de Passe',
      forgotPassword: 'Mot de passe oublié?',
      fillAllFields: 'Veuillez remplir tous les champs',
      invalidCredentials: 'E-mail ou mot de passe invalide. Veuillez vérifier vos identifiants et réessayer.',
      accountNotFound: 'Compte non trouvé. Veuillez vérifier votre e-mail ou créer un nouveau compte.',
      continueWithGoogle: 'Continuer avec Google'
    },
    
    // Options & Selections
    options: {
      short: 'Court',
      medium: 'Moyen',
      long: 'Long',
      basic: 'Basique',
      standard: 'Standard',
      detailed: 'Détaillé',
      trial: "Période d'Essai",
      proUser: 'Utilisateur Pro',
      concise: 'Concis',
      comprehensive: 'Complet',
      quickScan: 'Analyse Rapide',
      deepAnalysis: 'Analyse Approfondie',
      expertLevel: 'Niveau Expert'
    },
    
    // Profile & Settings
    profile: {
      profileInformation: 'Informations du Profil',
      companyInformation: 'Informations de l\'Entreprise',
      accountInformation: 'Informations du Compte',
      security: 'Sécurité',
      quickStats: 'Statistiques Rapides',
      edit: 'Modifier',
      save: 'Enregistrer',
      cancel: 'Annuler',
      saveChanges: 'Enregistrer les Modifications',
      changePassword: 'Changer le Mot de Passe',
      currentPassword: 'Mot de Passe Actuel',
      newPassword: 'Nouveau Mot de Passe',
      confirmNewPassword: 'Confirmer le Nouveau Mot de Passe',
      updatePassword: 'Mettre à Jour le Mot de Passe',
      cannotBeChanged: 'ne peut pas être modifié',
      phoneNumber: 'Numéro de Téléphone',
      company: 'Entreprise',
      department: 'Département',
      position: 'Poste',
      location: 'Emplacement',
      joinDate: 'Date d\'Adhésion',
      subscriptionStatus: 'Statut d\'Abonnement',
      timezone: 'Fuseau Horaire',
      accountRole: 'Rôle du Compte',
      administrator: 'Administrateur',
      user: 'Utilisateur',
      documentsUploaded: 'Documents Téléchargés',
      chatsCreated: 'Chats Créés',
      storageUsed: 'Stockage Utilisé',
      lastLogin: 'Dernière Connexion',
      today: 'Aujourd\'hui',
      manageProfile: 'Gérez votre profil et les paramètres de votre compte'
    }
  }
};

const TranslationContext = createContext();

export const useTranslation = () => {
  const context = useContext(TranslationContext);
  if (!context) {
    throw new Error('useTranslation must be used within a TranslationProvider');
  }
  return context;
};

export const TranslationProvider = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState(() => {
    // Load saved language preference or default to English
    const savedLanguage = localStorage.getItem('selectedLanguage');
    return savedLanguage || 'en';
  });

  // Save language preference to localStorage
  useEffect(() => {
    localStorage.setItem('selectedLanguage', currentLanguage);
  }, [currentLanguage]);

  // Translation function
  const t = (key) => {
    const keys = key.split('.');
    let value = translations[currentLanguage];
    
    for (const k of keys) {
      if (value && typeof value === 'object') {
        value = value[k];
      } else {
        break;
      }
    }
    
    // Fallback to English if translation not found
    if (!value && currentLanguage !== 'en') {
      let fallbackValue = translations.en;
      for (const k of keys) {
        if (fallbackValue && typeof fallbackValue === 'object') {
          fallbackValue = fallbackValue[k];
        } else {
          break;
        }
      }
      value = fallbackValue;
    }
    
    return value || key;
  };

  // Get available languages with their display names
  const getAvailableLanguages = () => [
    { code: 'en', name: 'English', nativeName: 'English' },
    { code: 'es', name: 'Spanish', nativeName: 'Español' },
    { code: 'fr', name: 'French', nativeName: 'Français' },
  ];

  // Change language function
  const changeLanguage = (languageCode) => {
    if (translations[languageCode]) {
      setCurrentLanguage(languageCode);
    }
  };

  const value = {
    currentLanguage,
    changeLanguage,
    t,
    getAvailableLanguages,
    availableLanguages: getAvailableLanguages()
  };

  return (
    <TranslationContext.Provider value={value}>
      {children}
    </TranslationContext.Provider>
  );
};

export default TranslationContext;
